import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowBigDownDash, Eye, SquarePen, Eraser, ChartPie, SatelliteDish } from 'lucide-react'
import * as XLSX from 'xlsx';
import FormAnalytics from '../components/Modals/FormAnalytics';
import ReportDeliveryConfig from '../components/ReportDeliveryConfig';
import DOMPurify from 'dompurify';
import ConnectFormModal from '../components/Modals/ConnectFormModal';

const MyForms = ({ theme }) => {
  const navigate = useNavigate();
  const [forms, setForms] = useState([]);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [filterType, setFilterType] = useState('org');
  const [filterValue, setFilterValue] = useState('');
  const [formToDelete, setFormToDelete] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [showAnalyticsModal, setShowAnalyticsModal] = useState(false);
  const [selectedFormId, setSelectedFormId] = useState(null);
  const [showConnectFormModal, setShowConnectFormModal] = useState(false);
  const [selectedFormToConnect, setSelectedFormToConnect] = useState(null);
  const [availableForms, setAvailableForms] = useState([]);
  const [formIdToConnect, setFormIdToConnect] = useState(null);
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);

  const fetchForms = async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        process.env.REACT_APP_URL_BASE_BACKEND + '/api/forms',
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error('Error al obtener los formularios');
      }
      const data = await response.json();
      console.log('Forms data actualizada:', data);
      setForms(data);
    } catch (error) {
      console.error('Error al obtener formularios:', error);
      setError('Error al obtener formularios');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchForms();
    checkUserRole();
  }, []);

  const handleShowAnalytics = formId => {
    setSelectedFormId(formId);
    setShowAnalyticsModal(true);
  };

  const checkUserRole = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        process.env.REACT_APP_URL_BASE_BACKEND + '/api/user/profile',
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      const userData = await response.json();
      setIsAdmin(userData.rol === 'Admin');
    } catch (error) {
      console.error('Error al verificar rol de usuario', error);
    }
  };

  const handleExportFormReport = async formId => {
    try {
      const token = localStorage.getItem('token');
      console.log('Generando reporte para el formulario con ID:', formId);

      // 1. Obtener las respuestas y los campos del formulario
      const formResponse = await fetch(
        process.env.REACT_APP_URL_BASE_BACKEND + `/forms/${formId}/responses`,
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!formResponse.ok) {
        throw new Error(
          `Error al obtener las respuestas del formulario: ${formResponse.status} ${formResponse.statusText}`
        );
      }

      const responseData = await formResponse.json();
      console.log('Datos completos:', responseData);

      // Verificar la estructura de los datos recibidos
      if (!responseData) {
        throw new Error('No se recibieron datos de la API');
      }

      // Extraer los arrays de respuestas y campos con valores por defecto para evitar errores
      const responses = responseData.responses || [];
      const fields = responseData.fields || [];
      const currentFormVersion = responseData.currentFormVersion || 1;

      console.log('Estructura de responseData:', Object.keys(responseData));
      console.log('Tipo de respuestas:', typeof responses);
      console.log('Respuestas:', responses);
      console.log('Campos actuales:', fields);
      console.log('Versión actual del formulario:', currentFormVersion);

      // Verificar que responses sea un array
      if (!Array.isArray(responses)) {
        console.error('Las respuestas no son un array:', responses);

        // Intenta determinar si hay otro campo que contenga las respuestas
        let possibleResponses = null;

        // Buscar en el objeto responseData algún campo que sea un array
        // y que podría contener las respuestas
        for (const key in responseData) {
          if (
            Array.isArray(responseData[key]) &&
            responseData[key].length > 0
          ) {
            console.log(
              `Posible campo de respuestas encontrado: ${key}`,
              responseData[key]
            );
            possibleResponses = responseData[key];
            break;
          }
        }

        // Si encontramos un posible campo de respuestas, usarlo
        if (possibleResponses) {
          console.log(
            'Usando campo alternativo como respuestas:',
            possibleResponses
          );
          // Continuar con possibleResponses
        } else {
          // Si responseData mismo es un array, podría ser que la estructura sea diferente
          if (Array.isArray(responseData)) {
            console.log(
              'responseData es un array, usándolo directamente como respuestas'
            );
            possibleResponses = responseData;
          } else {
            throw new Error(
              'No se pudo encontrar un formato válido de respuestas'
            );
          }
        }

        // Usar las respuestas alternativas si las encontramos
        if (possibleResponses) {
          responses = possibleResponses;
        } else {
          throw new Error(
            'El formato de respuestas no es válido y no se pudo recuperar'
          );
        }
      }

      // 2. Recopilar todos los campos (activos y archivados)
      const allFieldsMap = new Map();

      // Primero agregamos los campos activos
      fields.forEach(field => {
        // Asegurarse de que field tenga todas las propiedades necesarias
        if (field && field._id) {
          allFieldsMap.set(field._id, {
            id: field._id,
            label: field.label ? stripHtml(field.label) : `Campo ${field._id}`,
            originalLabel: field.label || `Campo ${field._id}`,
            isArchived: false,
          });
        }
      });

      // Luego identificamos todos los campos archivados de todas las respuestas
      responses.forEach(response => {
        if (!response || !response.responses) return;

        // Revisar campos archivados explícitos
        if (response.responses._archivedFields) {
          Object.entries(response.responses._archivedFields).forEach(
            ([fieldId, fieldData]) => {
              if (!allFieldsMap.has(fieldId)) {
                let label = fieldId;
                // Intentar obtener la etiqueta del campo archivado
                if (typeof fieldData === 'object' && fieldData.fieldLabel) {
                  label = stripHtml(fieldData.fieldLabel);
                }

                allFieldsMap.set(fieldId, {
                  id: fieldId,
                  label: `${label} (Eliminado)`,
                  originalLabel: label,
                  isArchived: true,
                });
              }
            }
          );
        }

        // También buscar cualquier clave en responses que no esté en los campos activos
        Object.keys(response.responses).forEach(key => {
          // Ignorar claves especiales como _archivedFields
          if (key.startsWith('_')) return;

          // Si la clave no es un ID de campo activo, podría ser un campo archivado
          const isActiveFieldId = fields.some(
            field => field && field._id === key
          );
          const isActiveFieldLabel = fields.some(
            field => field && field.label === key
          );

          if (
            !isActiveFieldId &&
            !isActiveFieldLabel &&
            !allFieldsMap.has(key)
          ) {
            allFieldsMap.set(key, {
              id: key,
              label: `${key} (Eliminado)`,
              originalLabel: key,
              isArchived: true,
            });
          }
        });
      });

      // Si no hay campos, no podemos generar un reporte
      if (allFieldsMap.size === 0) {
        throw new Error('No se encontraron campos para incluir en el reporte');
      }

      // 3. Preparar las cabeceras del Excel
      const headers = ['Fecha', 'Hora'];
      const fieldsList = Array.from(allFieldsMap.values());

      // Primero añadimos los campos activos
      fieldsList
        .filter(field => !field.isArchived)
        .forEach(field => {
          headers.push(field.label);
        });

      // Luego añadimos los campos archivados
      fieldsList
        .filter(field => field.isArchived)
        .forEach(field => {
          headers.push(field.label);
        });

      console.log('Cabeceras preparadas:', headers);

      // 4. Preparar los datos
      const data = [headers];

      // 5. Procesar cada respuesta
      responses.forEach(response => {
        // Asegurarse de que response tenga la estructura esperada
        if (!response) return;

        const timestamp = new Date(response.timestamp || Date.now());
        const row = [
          timestamp.toLocaleDateString(),
          timestamp.toLocaleTimeString(),
        ];

        const responseData = response.responses || {};

        // Procesar campos activos primero
        fieldsList
          .filter(field => !field.isArchived)
          .forEach(field => {
            let answer = '';
            const fieldId = field.id;

            // Intentar obtener la respuesta por ID
            if (responseData[fieldId] !== undefined) {
              answer = responseData[fieldId];
            }
            // También intentar por la etiqueta original
            else if (responseData[field.originalLabel] !== undefined) {
              answer = responseData[field.originalLabel];
            }

            // Limpiar y formatear la respuesta
            if (Array.isArray(answer)) {
              answer = answer.join(', ');
            } else if (typeof answer === 'object' && answer !== null) {
              try {
                answer = JSON.stringify(answer);
              } catch (e) {
                console.warn('Error al convertir objeto a string:', e);
                answer = '[Objeto complejo]';
              }
            }

            row.push(answer);
          });

        // Luego procesar campos archivados
        fieldsList
          .filter(field => field.isArchived)
          .forEach(field => {
            let answer = '';
            const fieldId = field.id;

            // Buscar en _archivedFields primero
            if (
              responseData._archivedFields &&
              responseData._archivedFields[fieldId]
            ) {
              const archivedField = responseData._archivedFields[fieldId];

              if (typeof archivedField === 'object' && archivedField !== null) {
                if (archivedField.value !== undefined) {
                  answer = archivedField.value;
                } else {
                  // Si no tiene propiedad value, intentamos usar todo el objeto
                  try {
                    answer = JSON.stringify(archivedField);
                  } catch (e) {
                    console.warn(
                      'Error al convertir objeto archivado a string:',
                      e
                    );
                    answer = '[Objeto complejo]';
                  }
                }
              } else {
                // Si no es un objeto, usamos el valor directamente
                answer = archivedField;
              }
            }
            // Si no está en _archivedFields, buscar directamente en las respuestas
            else if (responseData[fieldId] !== undefined) {
              answer = responseData[fieldId];
            }
            // También intentar por la etiqueta original
            else if (
              field.originalLabel &&
              responseData[field.originalLabel] !== undefined
            ) {
              answer = responseData[field.originalLabel];
            }

            // Limpiar y formatear la respuesta
            if (Array.isArray(answer)) {
              answer = answer.join(', ');
            } else if (typeof answer === 'object' && answer !== null) {
              try {
                answer = JSON.stringify(answer);
              } catch (e) {
                console.warn('Error al convertir objeto a string:', e);
                answer = '[Objeto complejo]';
              }
            }

            row.push(answer);
          });

        data.push(row);
      });

      // Verificar que tengamos datos para generar el Excel
      if (data.length <= 1) {
        throw new Error('No hay datos para generar el reporte');
      }

      // 6. Generar el Excel
      const ws = XLSX.utils.aoa_to_sheet(data);

      // Ajustar el ancho de las columnas
      const colWidths = headers.map(header => ({
        wch: Math.min(Math.max(header.length, 15), 50),
      }));
      ws['!cols'] = colWidths;

      // Crear y guardar el archivo
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Respuestas');

      const fileName = `reporte_formulario_${formId}_${
        new Date().toISOString().split('T')[0]
      }.xlsx`;
      XLSX.writeFile(wb, fileName);

      console.log('Archivo Excel generado exitosamente:', fileName);
      return fileName; // Devolvemos el nombre del archivo generado
    } catch (error) {
      console.error('Error detallado al generar el reporte:', error);
      alert(`Error al generar el reporte: ${error.message}`);
      throw error; // Re-lanzamos el error para manejo adicional si es necesario
    }
  };

  // Funciones auxiliares (implementar según necesidad)
  function stripHtml(text) {
    return text.replace(/<[^>]*>/g, '');
  }

  const handleFilterForms = async () => {
    try {
      const token = localStorage.getItem('token');
      const queryParams = new URLSearchParams({
        [filterType]: filterValue,
        startDate: startDate
          ? new Date(startDate).toISOString().split('T')[0]
          : '',
        endDate: endDate ? new Date(endDate).toISOString().split('T')[0] : '',
      });

      const response = await fetch(
        process.env.REACT_APP_URL_BASE_BACKEND + `/api/forms?${queryParams}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (!response.ok) {
        throw new Error('Error al filtrar los formularios');
      }

      const filteredForms = await response.json();
      setForms(filteredForms);
    } catch (error) {
      console.error('Error al filtrar formularios:', error);
    }
  };

  const handleViewForm = formId => {
    window.open(`/view-form/${formId}`, '_blank');
  };

  const handleDeleteForm = async () => {
    if (!formToDelete) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        process.env.REACT_APP_URL_BASE_BACKEND + `/api/forms/${formToDelete}`,
        {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        alert(errorData.error || 'Error al eliminar el formulario');
        return;
      }

      // Mostrar la alerta de éxito
      setShowSuccessAlert(true);
      // Ocultar la alerta después de 3 segundos
      setTimeout(() => {
        setShowSuccessAlert(false);
      }, 3000);
      fetchForms();
    } catch (error) {
      console.error('Error al eliminar el formulario:', error);
      alert('Hubo un error al eliminar el formulario');
    } finally {
      setFormToDelete(null);
    }
  };

  const handleEditForm = formId => {
    console.log('Editando formulario con ID:', formId); // Verifica que el ID sea correcto
    navigate(`/createform?id=${formId}`);
  };

  const handleConnectForm = async formId => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No hay token de autenticación');
      }
      if (formId === selectedFormToConnect) {
        alert('No puedes conectar un formulario consigo mismo.');
        return;
      }
      const response = await fetch(
        `${process.env.REACT_APP_URL_BASE_BACKEND}/api/forms/${formId}/connect`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            connectedForm: selectedFormToConnect,
          }),
        }
      );
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error al conectar los formularios');
      }
      const data = await response.json();
      alert('Formularios conectados exitosamente');
      setShowConnectFormModal(false);
      setSelectedFormToConnect('');
      fetchForms(); // Recargar la lista de formularios
    } catch (error) {
      console.error('Error:', error);
      alert(error.message || 'Error al conectar los formularios');
    }
  };
  const openConnectFormModal = formId => {
    setFormIdToConnect(formId);
    setSelectedFormToConnect(''); //
    setShowConnectFormModal(true);
    // Obtener formularios disponibles para conectar
    const available = forms.filter(
      f =>
        f._id !== formId &&
        !f.connectedForm &&
        f.fields.some(field => field.type === 'ticketGenerator')
    );
    setAvailableForms(available);
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center p-8">
        <div className="text-lg">Cargando formularios...</div>
      </div>
    );
  }

  if (error) {
    return <div className="p-4 text-red-600 bg-red-100 rounded">{error}</div>;
  }

  return (
    <div>
      {showSuccessAlert && (
        <div role="alert" className="alert alert-error mb-4">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-6 w-6 shrink-0 stroke-current"
            fill="none"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <span>El Formulario se ha eliminado correctamente</span>
        </div>
      )}
      <div className="flex space-x-4 justify-end">
        <select
          value={filterType}
          onChange={e => setFilterType(e.target.value)}
          className="select select-bordered"
        >
          <option value="org">Organización</option>
          <option value="createdBy">Usuario</option>
        </select>

        <input
          type="text"
          value={filterValue}
          onChange={e => setFilterValue(e.target.value)}
          placeholder={
            filterType === 'org'
              ? 'Nombre de Organización'
              : 'Nombre de Usuario'
          }
          className="input input-bordered"
        />
        <input
          type="date"
          value={startDate}
          onChange={e => setStartDate(e.target.value)} // Ya da un formato ISO yyyy-mm-dd
          className="input input-bordered"
        />
        <input
          type="date"
          value={endDate}
          onChange={e => setEndDate(e.target.value)} // Ya da un formato ISO yyyy-mm-dd
          className="input input-bordered"
        />
        <button onClick={handleFilterForms} className="btn btn-white ml-auto">
          Filtrar
        </button>
      </div>

      <h1 className="text-2x1 font-bold mb-4">Mis Formularios</h1>
      <div className="overflow-x-auto">
        <table className="table">
          <thead>
            <tr>
              <th>Titulo del Formulario</th>
              <th>Organizaciones</th>
              <th>Creado Por</th>
              <th>Estado de Conexión</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {forms.length > 0 ? (
              forms.map(form => (
                <tr key={form._id}>
                  <td>{stripHtml(form.formName) || 'Sin Nombre'}</td>
                  <td>{form.org || 'Sin Organizacion'}</td>
                  <td>{form.createdBy || 'Usuario Desconocido'}</td>
                  <td>
                    {form.connectedForm ? (
                      <span className="text-green-600 flex items-center">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-5 w-5 mr-1"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                            clipRule="evenodd"
                          />
                        </svg>
                        Conectado
                      </span>
                    ) : form.fields.some(
                        field => field.type === 'ticketSelector'
                      ) ? (
                      <span className="text-yellow-600 flex items-center">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-5 w-5 mr-1"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M10 18a8 8 0 100-16 8 8 0 000 16zM7 9a1 1 0 000 2h6a1 1 0 100-2H7z"
                            clipRule="evenodd"
                          />
                        </svg>
                        Sin conexión
                      </span>
                    ) : form.fields.some(
                        field => field.type === 'ticketGenerator'
                      ) ? (
                      <span className="text-blue-600 flex items-center">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-5 w-5 mr-1"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V7z"
                            clipRule="evenodd"
                          />
                        </svg>
                        Generador de IDs
                      </span>
                    ) : null}
                  </td>
                  <td className="space-x-2">
                    {isAdmin && (
                      <>
                        <button
                          className="btn btn-info btn-outline rounded-full mt-2 tooltip" data-tip="Editar"
                          onClick={() => handleEditForm(form._id)}
                        >
                          <SquarePen size={20}/>
                        </button>
                        <button
                          className="btn btn-error btn-outline rounded-full mt-2 tooltip" data-tip="Eliminar"
                          onClick={() => setFormToDelete(form._id)}
                        >
                          <Eraser size={20}/>
                        </button>
                      </>
                    )}
                    <button
                      className="btn btn-outline rounded-full mt-2 tooltip" data-tip="Ver Formulario"
                      onClick={() => handleViewForm(form._id)} 
                    >
                      <Eye size={20}/>
                    </button>

                    <button
                      className="btn btn-outline rounded-full mt-2 tooltip" data-tip="Descargar Reporte"
                      onClick={() => handleExportFormReport(form._id)}
                    >
                      < ArrowBigDownDash size={20} /> 
                    </button>
                    <button
                      className="btn btn-success btn-outline rounded-full mt-2 tooltip" data-tip="Analytics"
                      onClick={() => handleShowAnalytics(form._id)}
                    >
                      <ChartPie size={20}/>
                    </button>
                    {form.fields.some(
                      field => field.type === 'ticketSelector'
                    ) && (
                      <button
                        onClick={() => openConnectFormModal(form._id)}
                        className="btn btn-warning btn-outline rounded-full mt-2 tooltip" data-tip="Conectar"
                      >
                        <SatelliteDish size={20}/>
                      </button>
                    )}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="4" className="text-center py-4">
                  No hay formularios guardados
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {showAnalyticsModal && selectedFormId && (
        <div
          className={`fixed inset-0 flex items-center justify-center z-50 ${
            theme === 'dark'
              ? 'bg-black bg-opacity-75'
              : 'bg-black bg-opacity-50'
          }`}
        >
          <div
            className={`w-11/12 max-w-6xl max-h-screen overflow-auto rounded-lg shadow-lg ${
              theme === 'dark' ? 'bg-gray-800' : 'bg-white'
            }`}
          >
            <div className="flex justify-between items-center p-4 border-b">
              <h2
                className={`text-xl font-bold ${
                  theme === 'dark' ? 'text-white' : 'text-black'
                }`}
              >
                Análisis de Formulario
              </h2>
              <button
                onClick={() => setShowAnalyticsModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
            <div className="p-4">
              <FormAnalytics formId={selectedFormId} theme={theme} />
            </div>
          </div>
        </div>
      )}
      {formToDelete && (
        <div
          className={`fixed inset-0 flex items-center justify-center ${
            theme === 'dark'
              ? 'bg-black bg-opacity-75'
              : 'bg-black bg-opacity-50'
          }`}
        >
          <div
            className={`p-6 rounded-lg shadow-lg ${
              theme === 'dark'
                ? 'bg-gray-800 text-white'
                : 'bg-white text-black'
            }`}
          >
            <h3
              className={`text-lg font-semibold ${
                theme === 'dark' ? 'text-white' : 'text-black'
              }`}
            >
              ¿Seguro que quieres eliminar este formulario?
            </h3>
            <br />
            <p
              className={`text-gray-600 ${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
              }`}
            >
              Esta acción no se puede deshacer, asi que piense bien si
              eliminarlo
            </p>
            <div className="mt-4 flex justify-end space-x-2">
              <button
                className={`btn ${
                  theme === 'dark' ? 'btn-gray-700' : 'btn-gray'
                } btn-outline`}
                onClick={() => setFormToDelete(null)}
              >
                Cancelar
              </button>
              <button className="btn btn-error" onClick={handleDeleteForm}>
                Sí, eliminar
              </button>
            </div>
          </div>
        </div>
      )}
      <ConnectFormModal
        show={showConnectFormModal}
        onClose={() => setShowConnectFormModal(false)}
        availableForms={availableForms}
        selectedFormToConnect={selectedFormToConnect}
        onSelectForm={setSelectedFormToConnect}
        onConnect={() => handleConnectForm(formIdToConnect)}
      />
    </div>
  );
};

export default MyForms;
